import { useCallback, useMemo } from 'react';
import { FormControl, FormControlLabel, InputLabel, Select, Switch, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ZIP_CODE_FORMAT_ERROR } from '../../../shared/components/Forms/address-form.constants';
import { ParsedAddress } from '../../../shared/components/Forms/parse-google-address.util';
import { DeliveryZoneRow } from '../../../delivery-zones/types/delivery-zone.type';
import { REGION_PLACE_TYPES } from '../../constants/delivery-city-place.constants';
import { useLiveDeliveryZoneOptions } from '../../hooks/use-live-delivery-zone-options.hook';
import { DeliveryCity, DeliveryCityZone } from '../../types/delivery-city.type';
import { DELIVERY_CITY_NAME_ERROR } from '../../utils/delivery-city-form-rules.util';
import { DeliveryCityCard } from './DeliveryCityCard.component';

const ZONE_INPUT_ID = 'delivery-city-zone';

const toCityLabel = (address: ParsedAddress): string => address.city;

type Props = {
  updatedDeliveryCity: DeliveryCity;
  isArchived: boolean;
  isNameInvalid: boolean;
  isZipCodeInvalid: boolean;
  onNameChange: (name: string) => void;
  onZipCodeChange: (zipCode: string) => void;
  onZoneChange: (zone: DeliveryCityZone) => void;
  onIsActiveChange: (isActive: boolean) => void;
};

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    gap: '1rem',

    '& > *': {
      flex: 1,
    },
  },
  switchBase: {
    '&$switchChecked': {
      color: theme.palette.admin.primary.main,
    },
    '&$switchChecked + $switchTrack': {
      backgroundColor: theme.palette.admin.primary.main,
    },
  },
  switchChecked: {},
  switchTrack: {},
}));

const toZoneOptions = (liveZones: DeliveryZoneRow[], currentZone: DeliveryCityZone): DeliveryCityZone[] => {
  const options = liveZones.map(({ id, name }) => ({ id, name }));
  return options.some((option) => option.id === currentZone.id) ? options : [currentZone, ...options];
};

export function DeliveryCityIdentityCard({
  updatedDeliveryCity,
  isArchived,
  isNameInvalid,
  isZipCodeInvalid,
  onNameChange,
  onZipCodeChange,
  onZoneChange,
  onIsActiveChange,
}: Props) {
  const classes = useStyles();
  const { zoneOptions: liveZoneOptions } = useLiveDeliveryZoneOptions();
  const zoneOptions = useMemo(
    () => toZoneOptions(liveZoneOptions, updatedDeliveryCity.zone),
    [liveZoneOptions, updatedDeliveryCity.zone]
  );

  const handlePlaceSelected = useCallback(
    (address: ParsedAddress) => {
      onNameChange(address.city);
      if (address.zipCode !== '') onZipCodeChange(address.zipCode);
    },
    [onNameChange, onZipCodeChange]
  );

  const handleZoneChange = (zoneId: string) => {
    const selectedZone = zoneOptions.find((zone) => zone.id === zoneId);
    if (selectedZone) onZoneChange(selectedZone);
  };

  return (
    <DeliveryCityCard title="Identité">
      <FormControlLabel
        control={
          <Switch
            size="small"
            color="default"
            classes={{ switchBase: classes.switchBase, checked: classes.switchChecked, track: classes.switchTrack }}
            disabled={isArchived}
            checked={updatedDeliveryCity.isActive}
            onChange={(event) => onIsActiveChange(event.target.checked)}
          />
        }
        label="Actif"
      />
      <AddressAutocomplete
        id="delivery-city-name"
        label="Ville"
        size="small"
        types={REGION_PLACE_TYPES}
        toLabel={toCityLabel}
        value={updatedDeliveryCity.name}
        onChange={onNameChange}
        onAddressSelected={handlePlaceSelected}
        error={isNameInvalid}
        helperText={isNameInvalid ? DELIVERY_CITY_NAME_ERROR : undefined}
      />
      <div className={classes.row}>
        <TextField
          id="delivery-city-zip-code"
          label="Code postal"
          type="text"
          size="small"
          variant="outlined"
          inputProps={{ inputMode: 'numeric', maxLength: 5 }}
          error={isZipCodeInvalid}
          helperText={isZipCodeInvalid ? ZIP_CODE_FORMAT_ERROR : undefined}
          value={updatedDeliveryCity.zipCode}
          onChange={(event) => onZipCodeChange(event.target.value)}
        />
        <FormControl variant="outlined" size="small">
          <InputLabel htmlFor={ZONE_INPUT_ID}>Zone</InputLabel>
          <Select
            native
            label="Zone"
            value={updatedDeliveryCity.zone.id}
            inputProps={{ id: ZONE_INPUT_ID }}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => handleZoneChange(event.target.value)}
          >
            {zoneOptions.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.name}
              </option>
            ))}
          </Select>
        </FormControl>
      </div>
    </DeliveryCityCard>
  );
}
